
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Getter } from 'vuex-class'
import BaseLayout from '@/shared/components/BaseLayout.vue'
import CampaignService from '@/api/services/CampaignService'
import ExportOrderResponse, { ExportOrderResult } from '@/api/dto/ExportOrderResponse'
import { getErrorMessage, normalizeError } from '@/service/error'
import { getAccountId, getCampaignId } from '@/service/storage'
import { RootStateMutations } from '@/store/mutations'
import { redirectToRouteOnce } from '@/service/navigation'

type ExportMode = 'ids' | 'dates'
type CounterName = 'found' | 'created' | 'alreadyExists' | 'notFound' | 'errors'
const TERMINAL_STATUSES = ['Завершено', 'Завершено с ошибками', 'Ошибка']
const POLL_INTERVALS = [2000, 5000, 10000, 30000]
const LATEST_RETRY_INTERVALS = [5000, 10000, 30000]
const campaignService = new CampaignService()

@Component({
    components: { BaseLayout }
})
export default class ExportOrder extends Vue {
    @Getter('currentAccount') readonly accountId!: number
    @Getter('currentCampaign') readonly campaignId!: number
    mode: ExportMode = 'ids'
    orderIds = ''
    dateFrom: unknown = ''
    dateTo: unknown = ''
    result: ExportOrderResult | null = null
    submitting = false
    idsError = ''
    datesError = ''
    submitError = ''
    resultError = ''
    alreadyQueued = 0
    private timer: number | null = null
    private pollFailure = 0
    private latestFailure = 0
    private activeRunId = ''
    private pollGeneration = 0
    private observedContext = ''
    readonly counterNames: CounterName[] = ['found', 'created', 'alreadyExists', 'notFound', 'errors']

    get statusLabel (): string {
        if (!this.result) return ''
        const keys: Record<string, string> = { 'В очереди': 'queued', Выполняется: 'running', Завершено: 'completed', 'Завершено с ошибками': 'completedWithErrors', Ошибка: 'error' }
        return this.$t(`exportOrder.statuses.${keys[this.result.status] || 'error'}`).toString()
    }

    get isEmptyResult (): boolean {
        return Boolean(this.result && this.result.mode === 'dates' && this.result.counters.found === 0 && this.result.status === 'Завершено')
    }

    get todayDate (): string {
        const today = new Date()
        return `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`
    }

    @Watch('accountId')
    @Watch('campaignId')
    onContextChanged (): void {
        const context = `${this.accountId}:${this.campaignId}`
        if (context === this.observedContext) return
        this.observedContext = context
        this.invalidatePolling()
        this.result = null
        this.resultError = ''
        this.alreadyQueued = 0
        this.submitting = false
        this.restoreRun()
    }

    async created (): Promise<void> {
        const contextWasReady = Boolean(this.accountId && this.campaignId)
        this.observedContext = `${this.accountId}:${this.campaignId}`
        if (!this.accountId) {
            const accountId = getAccountId()
            if (!accountId) {
                await redirectToRouteOnce(this.$router, { name: 'accounts' })
                return
            }
            this.$store.commit(RootStateMutations.setAccount, accountId)
        }

        if (!this.campaignId) {
            const campaignId = getCampaignId()
            if (!campaignId) {
                await redirectToRouteOnce(this.$router, { name: 'account_campaigns' })
                return
            }
            this.$store.commit(RootStateMutations.setCampaign, campaignId)
        }

        if (contextWasReady) this.restoreRun()
    }

    beforeDestroy (): void { this.invalidatePolling() }

    async submit (): Promise<void> {
        this.clearErrors()
        if (!this.validate()) return
        if (!this.accountId || !this.campaignId) { this.submitError = this.$t('exportOrder.errors.context').toString(); return }
        this.invalidatePolling()
        const generation = this.pollGeneration
        const accountId = this.accountId
        const campaignId = this.campaignId
        this.result = null
        this.alreadyQueued = 0
        this.submitting = true
        try {
            const payload = this.mode === 'ids' ? { mode: 'ids', orderId: this.orderIds } : { mode: 'dates', dateFrom: this.toDateString(this.dateFrom), dateTo: this.toDateString(this.dateTo) }
            const response: ExportOrderResponse = await campaignService.exportOrder(campaignId, accountId, payload)
            if (!this.isCurrentContext(generation, accountId, campaignId)) return
            if (!response.runId) throw new Error(this.$t('exportOrder.errors.missingRunId').toString())
            this.alreadyQueued = response.alreadyQueued || 0
            this.activeRunId = response.runId
            await this.poll(true)
        } catch (error) {
            if (!this.isCurrentContext(generation, accountId, campaignId)) return
            const normalized = normalizeError(error)
            this.submitError = normalized.status === 404 ? this.$t('exportOrder.errors.notFound').toString() : normalized.status === 400 ? normalized.message : getErrorMessage(error)
        } finally {
            if (this.isCurrentContext(generation, accountId, campaignId)) this.submitting = false
        }
    }

    private validate (): boolean {
        if (this.mode === 'ids') {
            if (!/^\d+(,\d+)*$/.test(this.orderIds)) { this.idsError = this.$t('exportOrder.errors.ids').toString(); return false }
            return true
        }
        const from = this.toDateString(this.dateFrom); const to = this.toDateString(this.dateTo)
        if (!from || !to) { this.datesError = this.$t('exportOrder.errors.datesRequired').toString(); return false }
        const fromDate = new Date(`${from}T00:00:00`); const toDate = new Date(`${to}T00:00:00`)
        const days = Math.round((toDate.getTime() - fromDate.getTime()) / 86400000) + 1
        if (!this.isValidDate(from) || !this.isValidDate(to) || from > to || to > this.toDateString(new Date()) || days > 30) { this.datesError = this.$t('exportOrder.errors.datesRange').toString(); return false }
        return true
    }

    private isValidDate (value: string): boolean {
        const parts = value.split('-').map(Number)
        const date = new Date(parts[0], parts[1] - 1, parts[2])
        return date.getFullYear() === parts[0] && date.getMonth() === parts[1] - 1 && date.getDate() === parts[2]
    }

    private toDateString (value: unknown): string {
        if (typeof value === 'string') return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) return ''
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    }

    private async poll (immediate = false): Promise<void> {
        const generation = this.pollGeneration
        const accountId = this.accountId
        const campaignId = this.campaignId
        const runId = this.activeRunId
        if (!runId) return
        if (!immediate) await new Promise(resolve => { this.timer = window.setTimeout(resolve, POLL_INTERVALS[this.pollFailure]) })
        if (!this.isCurrentPoll(generation, accountId, campaignId, runId)) return
        this.timer = null
        try {
            const result = await campaignService.getExportOrderResult(campaignId, accountId, runId)
            if (!this.isCurrentPoll(generation, accountId, campaignId, runId)) return
            this.result = result
            this.resultError = ''; this.pollFailure = 0
            if (TERMINAL_STATUSES.indexOf(result.status) !== -1) { this.activeRunId = ''; return }
            this.schedulePoll()
        } catch (error) {
            if (!this.isCurrentPoll(generation, accountId, campaignId, runId)) return
            if (normalizeError(error).status === 404) { this.resultError = this.$t('exportOrder.errors.resultNotFound').toString(); this.activeRunId = ''; return }
            this.resultError = this.$t('exportOrder.errors.resultUnavailable').toString(); this.pollFailure = Math.min(this.pollFailure + 1, POLL_INTERVALS.length - 1); this.schedulePoll()
        }
    }

    private schedulePoll (): void { this.stopPolling(); this.timer = window.setTimeout(() => { this.poll(true).catch(() => undefined) }, POLL_INTERVALS[this.pollFailure]) }

    private restoreRun (): void {
        if (!this.accountId || !this.campaignId) return
        this.latestFailure = 0
        this.restoreLatest(this.pollGeneration, this.accountId, this.campaignId).catch(() => undefined)
    }

    private async restoreLatest (generation: number, accountId: number, campaignId: number): Promise<void> {
        if (!this.isCurrentContext(generation, accountId, campaignId)) return
        try {
            const result = await campaignService.getLatestExportOrderResult(campaignId, accountId)
            if (!this.isCurrentContext(generation, accountId, campaignId)) return
            this.result = result
            this.resultError = ''
            this.alreadyQueued = 0
            this.latestFailure = 0
            this.activeRunId = result.runId
            if (TERMINAL_STATUSES.indexOf(result.status) !== -1) {
                this.activeRunId = ''
                return
            }
            this.poll(true).catch(() => undefined)
        } catch (error) {
            if (!this.isCurrentContext(generation, accountId, campaignId)) return
            if (normalizeError(error).status === 404) {
                this.result = null
                this.resultError = ''
                this.activeRunId = ''
                this.stopPolling()
                return
            }
            this.resultError = this.$t('exportOrder.errors.resultUnavailable').toString()
            const interval = LATEST_RETRY_INTERVALS[Math.min(this.latestFailure, LATEST_RETRY_INTERVALS.length - 1)]
            this.latestFailure = Math.min(this.latestFailure + 1, LATEST_RETRY_INTERVALS.length - 1)
            this.stopPolling()
            this.timer = window.setTimeout(() => {
                this.timer = null
                this.restoreLatest(generation, accountId, campaignId).catch(() => undefined)
            }, interval)
        }
    }

    private stopPolling (): void { if (this.timer !== null) window.clearTimeout(this.timer); this.timer = null }
    private invalidatePolling (): void { this.pollGeneration += 1; this.stopPolling(); this.activeRunId = '' }
    private isCurrentPoll (generation: number, accountId: number, campaignId: number, runId: string): boolean {
        return this.pollGeneration === generation && this.accountId === accountId && this.campaignId === campaignId && this.activeRunId === runId
    }

    private isCurrentContext (generation: number, accountId: number, campaignId: number): boolean {
        return this.pollGeneration === generation && this.accountId === accountId && this.campaignId === campaignId
    }

    private clearErrors (): void { this.idsError = ''; this.datesError = ''; this.submitError = ''; this.resultError = '' }
}
